import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { generateHeadingId } from './utils';

/**
 * Remark plugin that converts inline HTML <br> / <br/> nodes into proper
 * mdast `break` nodes. This avoids the need for rehype-raw (which would
 * allow arbitrary HTML and open an XSS vector) while still supporting
 * line breaks inside table cells and other inline contexts.
 */
const remarkBreakTags = () => {
  const visit = (node: any) => {
    if (!node.children) return;
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      if (
        child.type === 'html' &&
        /^<br\s*\/?>$/i.test(child.value.trim())
      ) {
        node.children[i] = { type: 'break' };
      } else {
        visit(child);
      }
    }
  };
  return (tree: any) => {
    visit(tree);
  };
};

import '../../styles/markupStyles.css';

// Types
interface MarkupRendererProps {
  content: string;
  convertEmailToLink?: boolean;
}

// Type-safe wrapper for ReactMarkdown to handle React 19 compatibility
const Markdown = ReactMarkdown as React.FC<Options>;

// Helper functions
const extractText = (children: any): string => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.join('');
  return String(children || '');
};

// Component Factories
const createHeadingComponent = (tag: string, className: string) => {
  return (props: any) => {
    const text = extractText(props.children);
    const id = generateHeadingId(text);
    return React.createElement(tag, { id, className, ...props });
  };
};

// Custom Components
const CustomList = ({ children, parentname = 'ul' }: any) => {
  return (
    <li
      className="font-body font-normal !text-md !leading-xl !text-primary"
      style={{
        listStyleType: parentname === 'ol' ? 'decimal' : 'disc',
        marginLeft: '1.7rem'
      }}>
      {children}
    </li>
  );
};

const CustomLink = ({ href, children, convertEmailToLink }: any) => {
  // Check is valid email
  const isEmail = href?.startsWith('mailto:');

  if (isEmail && convertEmailToLink) {
    return (
      <a
        href={href}
        className="font-body font-semibold text-md leading-xl tracking-normal text-cic-blue-500 hover:underline [&_strong]:font-semibold [&_strong]:text-inherit [&_strong]:leading-inherit">
        {children}
      </a>
    );
  }

  // Handle regular HTTP/HTTPS links
  if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-body font-regular text-sm leading-lg tracking-normal text-cic-blue-500 underline [&_strong]:font-semibold [&_strong]:text-inherit [&_strong]:leading-inherit">
        {children}
      </a>
    );
  }

  return <span>{children}</span>;
};

const CustomTableCell = (props: any) => {
  const cellContent = React.Children.toArray(props.children).join('');

  if (cellContent.startsWith('^^^')) {
    const content = cellContent.replace('^^^', '').trim();
    return (
      <td
        data-span-group="true"
        data-span-content={content || ''}
        style={{ display: 'none' }}
        {...props}>
        {content}
      </td>
    );
  }

  return (
    <td
      className="text-left font-body font-normal text-s leading-l tracking-normal text-primary [&_*]:!font-normal [&_strong]:!font-semibold"
      {...props}
    />
  );
};

/**
 * Walks the React element tree to compute rowspan groups for cells using
 * the ^^^ convention, then returns cloned children with declarative rowSpan
 * attributes. No DOM manipulation needed.
 *
 * At this point in the tree, <td> elements are still unrendered
 * <CustomTableCell> elements, so we detect ^^^ from the raw children prop
 * (the markdown cell text) rather than from data-* attributes.
 */
const computeSpannedChildren = (
  children: React.ReactNode
): React.ReactNode => {
  const childArray = React.Children.toArray(children);

  const tbodyIndex = childArray.findIndex(
    (child: any) => child?.type === 'tbody'
  );
  if (tbodyIndex === -1) return children;

  const tbody = childArray[tbodyIndex] as React.ReactElement<any>;
  const rows = React.Children.toArray(
    tbody.props.children
  ) as React.ReactElement<any>[];
  if (rows.length === 0) return children;

  // Build 2D grid of cell metadata by reading the raw children prop
  // (the markdown text before CustomTableCell processes it)
  const grid = rows.map((row: React.ReactElement<any>) => {
    const cells = React.Children.toArray(
      row.props.children
    ) as React.ReactElement<any>[];
    return cells.map((cell) => {
      const cellText = React.Children.toArray(cell.props?.children).join('');
      const isSpan = cellText.startsWith('^^^');
      const content = isSpan ? cellText.replace('^^^', '').trim() : '';
      return { isSpan, content, element: cell };
    });
  });

  const numCols = grid[0]?.length || 0;
  if (numCols === 0) return children;

  // Compute rowSpan and visibility per cell
  type CellSpan = { rowSpan: number; hidden: boolean; content: string };
  const spans: CellSpan[][] = grid.map((row) =>
    row.map(() => ({ rowSpan: 1, hidden: false, content: '' }))
  );

  for (let col = 0; col < numCols; col++) {
    let groupStart = -1;
    let groupContent = '';

    const finalizeGroup = (endRow: number) => {
      if (groupStart === -1) return;
      spans[groupStart][col] = {
        rowSpan: endRow - groupStart,
        hidden: false,
        content: groupContent
      };
      for (let r = groupStart + 1; r < endRow; r++) {
        spans[r][col] = { rowSpan: 1, hidden: true, content: '' };
      }
      groupStart = -1;
      groupContent = '';
    };

    for (let row = 0; row < grid.length; row++) {
      const cell = grid[row]?.[col];
      if (!cell) continue;
      if (cell.isSpan) {
        if (groupStart === -1) groupStart = row;
        if (cell.content) groupContent = cell.content;
      } else {
        finalizeGroup(row);
      }
    }
    finalizeGroup(grid.length);
  }

  // Rebuild rows with correct rowSpan / visibility.
  // For spanned cells we emit a plain <td> instead of cloning the
  // CustomTableCell element, because CustomTableCell would hide it.
  const newRows = rows.map((row: React.ReactElement<any>, rowIdx: number) => {
    const cells = React.Children.toArray(
      row.props.children
    ) as React.ReactElement<any>[];

    const newCells: React.ReactElement[] = [];
    cells.forEach((cell, colIdx) => {
      const info = spans[rowIdx]?.[colIdx];
      if (!info) return;

      if (info.hidden) {
        // Keep hidden cells in the DOM (display:none) so that
        // :first-child / :last-child CSS selectors on siblings
        // remain correct and column borders are preserved.
        newCells.push(
          React.createElement('td', {
            key: cell.key ?? `hidden-${rowIdx}-${colIdx}`,
            style: { display: 'none' }
          })
        );
        return;
      }

      if (info.rowSpan > 1) {
        // Render a plain <td> with the merged content and rowSpan
        newCells.push(
          React.createElement(
            'td',
            {
              key: cell.key ?? `span-${rowIdx}-${colIdx}`,
              rowSpan: info.rowSpan,
              style: { verticalAlign: 'middle', textAlign: 'left' },
              className:
                'text-left font-body font-normal text-s leading-l tracking-normal text-primary'
            },
            info.content
          )
        );
      } else {
        newCells.push(cell);
      }
    });

    return React.cloneElement(
      row,
      { key: row.key ?? `row-${rowIdx}` },
      ...newCells
    );
  });

  const newTbody = React.cloneElement(tbody, {}, ...newRows);
  const result = [...childArray];
  result[tbodyIndex] = newTbody;
  return result;
};

const CustomTable = ({ children }: any) => {
  const processedChildren = React.useMemo(
    () => computeSpannedChildren(children),
    [children]
  );

  return (
    <table className="clean-table mb-6" style={{ tableLayout: 'fixed' }}>
      {processedChildren}
    </table>
  );
};

// Main Component
const MarkupRenderer: React.FC<MarkupRendererProps> = ({
  content: rawContent,
  convertEmailToLink = false
}) => {
  // Replace literal \n with line breaks, render escaped leading hyphens as text,
  // and escape N) list markers
  const content = rawContent
    .replace(/\\n/g, '<br/>')
    .replace(/^\\- /gm, '&#45; ')
    .replace(/^(\d+)\) /gm, '$1\\) ');
  // Custom component for handling h2 sections
  const CustomH2 = (props: any) => {
    // Extract the heading text and generate an ID
    const text = extractText(props.children);
    const id = generateHeadingId(text);

    return React.createElement('h2', {
      id,
      className:
        'w-full font-display font-semibold text-3xl leading-8xl tracking-normal text-primary',
      ...props
    });
  };

  // Custom component for handling h3 sections
  const CustomH3 = (props: any) => {
    return (
      <h3
        className="font-display font-semibold text-2xl leading-4xl tracking-normal text-primary w-full h-[32px]"
        {...props}
      />
    );
  };

  const CustomH4 = (props: any) => {
    return (
      <h4
        className="font-display font-semibold text-xl leading-lg tracking-normal text-primary w-full"
        {...props}
      />
    );
  };

  // Process content by grouping H3 sections
  const processH3Content = (content: string) => {
    if (!content) return [];

    // Split by h3 headings (### in markdown)
    const sections = content.split(/^### /m);

    // First part might not start with ###, handle it separately
    const preH3Content = sections[0];
    const h3Sections = sections.slice(1);

    const result = [];

    // Add content before any H3 if it exists
    if (preH3Content.trim()) {
      result.push(
        <div key="pre-h3-content" className="space-y-2xl">
          {' '}
          {/* Always 2xl for regular content */}
          <Markdown
            remarkPlugins={[remarkGfm, remarkBreaks, remarkBreakTags]}
            components={baseMarkdownComponents}>
            {preH3Content}
          </Markdown>
        </div>
      );
    }

    // Process each H3 section
    h3Sections.forEach((section, index) => {
      const lines = section.split('\n');
      const h3Title = lines[0]; // First line is the h3 title
      const h3Content = lines.slice(1).join('\n'); // Rest is the content

      result.push(
        <div key={`h3-section-${index}`} className="!mt-7xl space-y-2xl">
          <CustomH3>{h3Title}</CustomH3>
          <Markdown
            remarkPlugins={[remarkGfm, remarkBreaks, remarkBreakTags]}
            components={baseMarkdownComponents}>
            {h3Content}
          </Markdown>
        </div>
      );
    });

    return result;
  };

  // Split content by h2 headings to create separate sections
  const renderStructuredContent = () => {
    // If no content, return empty div
    if (!content) return <div></div>;

    // Split content by h2 headings (## in markdown)
    const sections = content.split(/^## /m);

    // First part might not start with ##, handle it separately
    const preH2Content = sections[0];
    const h2Sections = sections.slice(1);

    return (
      <div className="flex flex-col gap-y-9xl">
        {/* Render any content before the first h2 */}
        {preH2Content && preH2Content.trim() && (
          <div className="space-y-2xl">
            {processH3Content(preH2Content)}
          </div>
        )}

        {/* Render each h2 section with its content */}
        {h2Sections.map((section, index) => {
          // Split the section into the h2 title and its content
          const lines = section.split('\n');
          const h2Title = lines[0]; // First line is the h2 title
          const h2Content = lines.slice(1).join('\n'); // Rest is the content

          return (
            <div key={`h2-section-${index}`} className="flex flex-col gap-y-2xl">
              <CustomH2>{h2Title}</CustomH2>
              <div className="space-y-2xl">
                {processH3Content(h2Content)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Base Markdown Components Configuration
  const baseMarkdownComponents = {
    // Headers
    h1: createHeadingComponent('h1', 'text-3xl font-bold'),
    // h2 and h3 are handled separately
    h3: (props: any) => null, // Skip default rendering since we handle h3 manually
    h4: (props: any) => <CustomH4 {...props} />,

    // Text Elements
    p: (props: any) => (
      <p
        className="w-full font-body font-regular text-md leading-lg tracking-normal text-primary mt-2xl first:mt-0"
        {...props}
      />
    ),
    em: (props: any) => <em className="italic" {...props} />,
    strong: (props: any) => (
      <strong
        className="font-body font-semibold text-m leading-xl tracking-normal text-primary"
        {...props}
      />
    ),
    hr: () => <div className="!mt-9xl" />,

    // Lists
    li: (props: any) => <CustomList {...props} />,
    ol: (props: any) => {
      const childrenWithProps = React.Children.map(props.children, (child) => {
        if (React.isValidElement(child) && child?.key?.startsWith('li')) {
          return React.cloneElement(child, { parentname: 'ol' } as any);
        }
        return child;
      });
      return <ol {...props}>{childrenWithProps}</ol>;
    },
    ul: (props: any) => <ul className="w-full" {...props} />,

    // Tables
    table: CustomTable,
    thead: (props: any) => <thead {...props} />,
    th: (props: any) => {
      const childArray = React.Children.toArray(props.children);
      // Extract width hint from string children only
      let width: string | undefined;
      const processedChildren = childArray.map((child) => {
        if (typeof child === 'string') {
          const widthMatch = child.match(/\{(\d+(?:px|%))\}/);
          if (widthMatch) {
            width = widthMatch[1];
            return child.replace(/\s*\{\d+(?:px|%)\}/, '');
          }
        }
        return child;
      });
      return (
        <th
          {...props}
          className="!font-medium !text-xs !leading-xs !text-secondary h-[18px]"
          style={width ? { ...(props.style || {}), width } : props.style}>
          {processedChildren}
        </th>
      );
    },
    td: CustomTableCell,
    a: (props: any) => CustomLink({ ...props, convertEmailToLink })
  };

  return renderStructuredContent();
};

export default MarkupRenderer;
